import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToStepsSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import { TRANSLATION_FAQ_CATEGORIES } from "@/content/translation-corpus/faq-defs";
import { TRANSLATION_DOC_LANDINGS } from "@/content/translation-corpus/documents";
import { TRANSLATION_COUNTRY_NAMES as TRANSLATION_COUNTRY_LANDINGS } from "@/content/translation-corpus/names.gen";

const STEPS = [
  {
    title: "Confirm which certification system the receiver requires",
    body: "Thai MFA legalization, a sworn translator on a foreign court register, NAATI for Australia, or a translator's declaration. The wrong system is the most common reason a translation is rejected.",
  },
  {
    title: "Send a clear scan of the source document",
    body: "The translation must match the issuing authority's exact spelling of names, dates and document numbers. We flag missing prior certifications, such as an Amphur or Department of Provincial Administration stamp, before translating.",
  },
  {
    title: "Produce the certified translation",
    body: "The translator prepares the target-language text with a signed certificate of accuracy in the layout the receiving authority accepts, keeping names consistent with your passport.",
  },
  {
    title: "Legalize the translation where required",
    body: "For Thai documents going abroad, file at the Legalization Division, Department of Consular Affairs: THB 200 per stamp standard, 2-3 working days, or THB 400 express when lodged before 11:30.",
  },
  {
    title: "Add the destination step, embassy or Apostille",
    body: "Until 28 February 2027 most destinations still need their embassy in Bangkok to countersign. After that date Hague Convention states accept a single Apostille instead of the embassy step.",
  },
];

export const Route = createFileRoute("/en/translation/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/translation-corpus/faq-en");
    return { faqs: m.TRANSLATION_FAQ_EN.slice(0, 12) };
  },
  component: TranslationHubEn,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const faqs = loaderData?.faqs ?? [];
    return {
      ...pageHeadForProvince({
        title: "Certified Translation Thailand — Documents, Languages & Legalization",
        description:
          "Hub for certified translation in Thailand: pick your document type or destination country, see which certification system applies, MFA stamp fees and turnaround, and the 2027 Apostille route.",
        path: "/en/translation",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/translation" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Certified Translation", url: `${site}/en/translation` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildHowToStepsSchema({
              name: "How to get a certified translation accepted in Thailand or abroad",
              description: "The real working order from choosing the certification system to the destination step",
              path: "/en/translation",
              steps: STEPS.map((s, i) => ({ step: i + 1, title: s.title, body: s.body })),
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Certified translation by destination country",
            itemListElement: TRANSLATION_COUNTRY_LANDINGS.map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/en/translation/country/${c.slug}`,
              name: c.nameEn,
            })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/translation")) },
      ],
    };
  },
});

function TranslationHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content" className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Certified Translation</span>
        </nav>
        <h1 className="text-3xl font-extrabold md:text-4xl">
          Certified Translation in Thailand — By Document, Language and Destination
        </h1>
        <p className="mt-4 max-w-3xl text-muted-foreground" data-speakable="true">
          A certified translation is only accepted when it uses the certification system the receiving authority
          recognises — Thai MFA legalization, a sworn translator on a foreign court register, or NAATI for Australia.
          MFA stamps cost THB 200 each with a 2-3 working day turnaround, or THB 400 for same-day express lodged
          before 11:30. Until 28 February 2027 most destinations still require their embassy in Bangkok to countersign.
        </p>

        <section className="mt-10">
          <h2 className="mb-4 text-xl font-bold">The five working steps</h2>
          <ol className="space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.title} id={`step-${i + 1}`} className="rounded-lg border bg-card p-4">
                <div className="font-semibold">{i + 1}. {s.title}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Knowledge topics and FAQs</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {TRANSLATION_FAQ_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-lg border bg-card p-4">
                <a href={`/en/translation/faq/${c.slug}`} className="font-semibold hover:underline">{c.titleEn}</a>
                <p className="mt-1 text-sm text-muted-foreground">{c.descEn}</p>
              </li>
            ))}
          </ul>
          <a href="/en/translation/faq" className="mt-4 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">
            Browse every question and answer
          </a>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Translation by document type</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {TRANSLATION_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/en/translation/document/${d.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {d.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Translation by destination country</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {TRANSLATION_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/en/translation/country/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.nameEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mt-10 space-y-3">
          <h2 className="text-xl font-bold">Frequently asked questions</h2>
          {faqs.map((f: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 4 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{f.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Check your requirement before you order</h2>
          <p className="text-sm text-muted-foreground">
            Send the document and tell us who will receive it — we confirm the certification system and the full
            legalization chain first.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Talk to our team</a>
        </section>
        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </div>
  );
}
